import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useState,
  type ReactNode,
} from "react";
import {
  fetchSession,
  refreshToken,
  login,
  logout,
  loginWithPassword,
  registerWithPassword,
  type SessionUser,
  type AuthResult,
} from "./auth-client";
import { setToken, setRefreshCallback } from "../api/token-store";
import styles from "./AuthContext.module.css";

// Parse a JWT payload without verification (claims already verified by WorkOS).
function parseJwt(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3 || !parts[1]) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

interface User {
  id: string;
  email: string;
  name: string;
  image?: string;
  roles: string[];
}

// True if the token expires within the next 5 minutes (or is missing).
function isTokenExpiringSoon(token: string | undefined): boolean {
  if (!token) return true;
  const claims = parseJwt(token);
  if (!claims || typeof claims.exp !== "number") return true;
  return (claims.exp as number) * 1000 < Date.now() + 5 * 60 * 1000;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  token: string | undefined;
  /** Navigate to the custom login page. */
  login: () => void;
  logout: () => void;
  /** Email + password sign-in; updates context on success. */
  signIn: (email: string, password: string) => Promise<AuthResult>;
  /** Email + password sign-up; updates context on success. */
  signUp: (input: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    phoneVerificationToken?: string;
  }) => Promise<AuthResult>;
  /** Rotate the session and refresh the in-memory token (used on 401). */
  updateToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function toUser(sessionUser: SessionUser): User {
  return {
    id: sessionUser.id,
    email: sessionUser.email,
    name: sessionUser.name,
    roles: sessionUser.roles || [],
  };
}

function AuthContextProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);
  const [sessionNoticeVisible, setSessionNoticeVisible] = useState(false);

  // Pull the current session (user + access token) and sync local state + store.
  const loadSession = useCallback(async () => {
    const { user: sessionUser, accessToken: token } = await fetchSession();
    if (sessionUser) {
      setUser(toUser(sessionUser));
      setAccessToken(token);
      setToken(token);
      return true;
    }
    setUser(null);
    setAccessToken(undefined);
    setToken(undefined);
    return false;
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await loadSession();
      } catch (error) {
        console.error("Failed to load session:", error);
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [loadSession]);

  const handleRefresh = useCallback(async () => {
    const ok = await refreshToken();
    if (ok) {
      setSessionNoticeVisible(false);
      await loadSession();
    } else if (user) {
      setSessionNoticeVisible(true);
    }
    return ok;
  }, [loadSession, user]);

  useEffect(() => {
    setRefreshCallback(handleRefresh);
  }, [handleRefresh]);

  // Proactive refresh: every minute, refresh if the token is expiring soon.
  useEffect(() => {
    if (!user) return;
    const check = async () => {
      if (isTokenExpiringSoon(accessToken)) await handleRefresh();
    };
    void check();
    const interval = setInterval(check, 60 * 1000);
    return () => clearInterval(interval);
  }, [user, accessToken, handleRefresh]);

  const signIn = useCallback(
    async (email: string, password: string): Promise<AuthResult> => {
      const result = await loginWithPassword(email, password);
      if (result.ok) {
        setUser(toUser(result.user));
        setAccessToken(result.accessToken);
        setToken(result.accessToken);
      }
      return result;
    },
    [],
  );

  const signUp = useCallback(
    async (input: {
      email: string;
      password: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
      phoneVerificationToken?: string;
    }): Promise<AuthResult> => {
      const result = await registerWithPassword(input);
      if (result.ok) {
        setUser(toUser(result.user));
        setAccessToken(result.accessToken);
        setToken(result.accessToken);
      }
      return result;
    },
    [],
  );

  const value: AuthContextValue = {
    isAuthenticated: !isLoading && !!user,
    isLoading,
    user,
    token: accessToken,
    login,
    logout,
    signIn,
    signUp,
    updateToken: handleRefresh,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {sessionNoticeVisible && (
        <div className={styles.sessionToast} role="alert">
          <div>
            <strong>SESSION EXPIRED</strong>
            <span>Please sign in again to continue.</span>
          </div>
          <button type="button" onClick={login}>
            SIGN IN
          </button>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  return <AuthContextProvider>{children}</AuthContextProvider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
